import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import SearchLeadershipTeam from '../../components/SearchLeadershipTeam';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// TYPES
import {
	LeadershipTeamMembersLeadershipTeamMember,
	LeadershipTeamSearchUserSearchSummary,
	LeadershipTeamAssignCommand
} from '../../api/models';

const LeadershipTeam: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Member Id
	const [memberDetail, setMemberDetail] = React.useState<LeadershipTeamMembersLeadershipTeamMember>({});

	// Set up Data state
	const [leadershipTeamData, setLeadershipTeamData] = React.useState<LeadershipTeamMembersLeadershipTeamMember[]>([]);

	// function to  Get leadership team data from api call
	const getLeadershipTeamData = async () => {
		setIsLoading(true);

		const response = await apibridge.getLeadershipTeamData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setLeadershipTeamData(response.result);
		}
	};

	React.useEffect(() => {
		getLeadershipTeamData();
	}, []);

	const removeMemberHandler = async (item: LeadershipTeamMembersLeadershipTeamMember) => {
		setMemberDetail(item);

		setShowRemoveModal(true);
	};

	const addLeadershipHandler = async (item: LeadershipTeamSearchUserSearchSummary) => {
		if (item !== undefined && item.id !== undefined) {
			setIsLoading(true);

			const data: LeadershipTeamAssignCommand = { userId: item.id };

			const response = await apibridge.addLeadershipTeamMember(data);

			response.result = response.result || [];

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				getLeadershipTeamData();
			}
		}
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{leadershipTeamData && leadershipTeamData.length > 0 ? (
									<section className="info-section mtop-0">
										<div className="section-header">
											<h2 className="header-name color-dark">Members</h2>
										</div>

										<ul>
											{leadershipTeamData.map((item) => {
												return (
													<li key={item.userId} className="row-container">
														<div className="row detail-row">
															<div className="col-auto pdl-0 pdr-0">
																<CircleImage
																	firstName={item.firstName}
																	lastName={item.lastName}
																	src={item.profileImage}
																/>
															</div>

															<div className="col-md-auto full-width">
																<div className="row detail-info center">
																	<div className="col-lg-4 detail-name">
																		{item.firstName} {item.lastName}
																		&nbsp;
																		{item.isCurrentUser && <strong>(You)</strong>}
																	</div>

																	<div className="col-lg-8 detail-status">
																		<span className="label">{item.position}</span>
																	</div>
																</div>
															</div>

															<div className="col-auto pdr-0 link-group wide">
																{isOrganisationAdmin ? (
																	<>
																		{isOrganisationActive && (
																			<Button onClick={() => removeMemberHandler(item)} className="btn-text remove">
																				Remove from team
																			</Button>
																		)}

																		<Link className="btn-text pdr-0" to={`/staff/${item.userProfileId}`}>
																			View
																		</Link>
																	</>
																) : null}
															</div>
														</div>
													</li>
												);
											})}
										</ul>
									</section>
								) : (
									<div className="detail-prompt">You have not yet added any Leadership Team Members.</div>
								)}

								{isOrganisationAdmin && isOrganisationActive ? (
									<section className="people-search-wrapper">
										<h2 className="search-heading">Add a member</h2>

										<SearchLeadershipTeam
											placeholder="Begin typing a staff name..."
											onChange={(item: LeadershipTeamSearchUserSearchSummary) => addLeadershipHandler(item)}
										/>
									</section>
								) : null}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove Leadership team member">
				<RemoveModal memberDetail={memberDetail} getLeadershipTeamData={getLeadershipTeamData} />
			</GlobalModal>
		</div>
	);
};

export default LeadershipTeam;
